import React, { useState, useEffect, useRef } from 'react';
import { FormData } from '../types';
import RotatingStats from './RotatingStats';
import { AD_PRICE_FORMATTED } from '../utils/config';

interface TelegramPreviewProps {
  formData: FormData;
}

// Convert Telegram markdown to HTML
const convertTelegramMarkdownToHtml = (text: string): string => {
  if (!text) return '';
  
  let html = text;
  
  // Convert **bold** to <strong>bold</strong>
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert *italic* to <em>italic</em>
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert __underline__ to <u>underline</u>
  html = html.replace(/__(.*?)__/g, '<u>$1</u>');
  
  // Convert ~~strikethrough~~ to <s>strikethrough</s>
  html = html.replace(/~~(.*?)~~/g, '<s>$1</s>');
  
  // Convert `code` to <code>code</code>
  html = html.replace(/`(.*?)`/g, '<code>$1</code>');
  
  // Convert line breaks to <br>
  html = html.replace(/\n/g, '<br>');
  
  return html;
};

const TelegramPreview: React.FC<TelegramPreviewProps> = ({ formData }) => {
  const [reactions, setReactions] = useState({
    likes: 0,
    thumbs: 0,
    hearts: 0,
    views: 0
  });

  // Animation state
  const [animationPhase, setAnimationPhase] = useState(1);
  const [animationInterval, setAnimationInterval] = useState(1000);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Define stats for rotation
  const channelStats = [
    {
      text: "57,638 subscribers",
      emoji: "👥",
      highlight: "57,638"
    },
    {
      text: `Ad placement only ${AD_PRICE_FORMATTED}`,
      emoji: "💰",
      highlight: AD_PRICE_FORMATTED
    },
    {
      text: "68% live in France",
      emoji: "🇫🇷",
      highlight: "68%"
    },
    {
      text: "Average age: 34",
      emoji: "📊",
      highlight: "34"
    },
    {
      text: "82% employed",
      emoji: "💼",
      highlight: "82%"
    },
    {
      text: "8.2% engagement rate",
      emoji: "📈",
      highlight: "8.2%"
    },
    {
      text: "45K daily views",
      emoji: "👁️",
      highlight: "45K"
    }
  ];

  // Advanced reaction animation with phases
  useEffect(() => {
    const shouldShowAd = formData.adTextContent.trim() && formData.adTextContent !== '';
    
    const animateReactions = () => {
      if (shouldShowAd) {
        setReactions(prev => {
          let newReactions = { ...prev };
          
          // Phase 1: Rapid growth to big numbers (0-200 range)
          if (animationPhase === 1) {
            newReactions.likes += Math.floor(Math.random() * 15) + 5; // 5-20 per tick
            newReactions.thumbs += Math.floor(Math.random() * 8) + 3; // 3-11 per tick
            newReactions.hearts += Math.floor(Math.random() * 6) + 2; // 2-8 per tick
            newReactions.views += Math.floor(Math.random() * 50) + 20; // 20-70 per tick
            
            // Switch to phase 2 when likes reach ~200
            if (newReactions.likes >= 200) {
              setAnimationPhase(2);
              setAnimationInterval(2000); // Slow down to 2 seconds
            }
          }
          // Phase 2: Medium growth (200-500 range)
          else if (animationPhase === 2) {
            newReactions.likes += Math.floor(Math.random() * 8) + 2; // 2-10 per tick
            newReactions.thumbs += Math.floor(Math.random() * 4) + 1; // 1-5 per tick
            newReactions.hearts += Math.floor(Math.random() * 3) + 1; // 1-4 per tick
            newReactions.views += Math.floor(Math.random() * 25) + 10; // 10-35 per tick
            
            // Switch to phase 3 when likes reach ~500
            if (newReactions.likes >= 500) {
              setAnimationPhase(3);
              setAnimationInterval(4000); // Slow down to 4 seconds
            }
          }
          // Phase 3: Slow growth (500+ range)
          else if (animationPhase === 3) {
            if (Math.random() < 0.6) { // 60% chance to increment
              newReactions.likes += Math.floor(Math.random() * 3) + 1; // 1-3 per tick
            }
            if (Math.random() < 0.4) { // 40% chance to increment
              newReactions.thumbs += Math.floor(Math.random() * 2) + 1; // 1-2 per tick
            }
            if (Math.random() < 0.3) { // 30% chance to increment
              newReactions.hearts += Math.floor(Math.random() * 2) + 1; // 1-2 per tick
            }
            if (Math.random() < 0.7) { // 70% chance to increment
              newReactions.views += Math.floor(Math.random() * 15) + 5; // 5-20 per tick
            }
          }
          
          return newReactions;
        });
      } else {
        // Reset when ad is hidden
        setReactions({
          likes: 0,
          thumbs: 0,
          hearts: 0,
          views: 0
        });
        setAnimationPhase(1);
        setAnimationInterval(1000);
      }
    };

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set new interval based on current phase
    intervalRef.current = setInterval(animateReactions, animationInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [formData.adTextContent, animationPhase, animationInterval]);

  // Format numbers for display (e.g., 1.2K, 15.3K)
  const formatCount = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    } else {
      return num.toString();
    }
  };

  const shouldShowAd = formData.adTextContent.trim() && formData.adTextContent !== '';

  return (
    <div className="telegram-preview">
      <div className="telegram-header">
        <div className="channel-avatar"></div>
        <div className="channel-info">
          <div className="channel-name-preview">Neodia</div>
          <div className="channel-meta">
            <RotatingStats stats={channelStats} />
          </div>
        </div>
      </div>
      
      <div className="telegram-messages">
        {/* Your Ad Preview */}
        {shouldShowAd && (
          <div className="telegram-message your-ad">
            <div className="message-content">
              {formData.imagePreview && (
                <div className="ad-image-preview">
                  <img 
                    src={formData.imagePreview} 
                    alt="Advertisement"
                  />
                </div>
              )}
              {(formData.adContent || formData.adTextContent) && (
                <div 
                  className="ad-body-preview"
                  dangerouslySetInnerHTML={{ 
                    __html: convertTelegramMarkdownToHtml(formData.adContent || formData.adTextContent) 
                  }}
                />
              )}
            </div>
            <div className="message-footer">
              <div className="reactions">
                <span className="reaction">😀 {formatCount(reactions.likes)}</span>
                <span className="reaction">👍 {formatCount(reactions.thumbs)}</span>
                <span className="reaction">❤️ {formatCount(reactions.hearts)}</span>
              </div>
              <div className="message-meta">
                <span className="views">👁 {formatCount(reactions.views)}</span>
                <span className="message-time">now</span>
              </div>
            </div>
            {formData.buttonText && (
              <button className="ad-button-preview">
                {formData.buttonText}
              </button>
            )}
          </div>
        )}

        {/* Sample Messages */}
        <div className="telegram-message">
          <div className="message-content">
            <div className="post-text">
              🚨 BREAKING: Nouvelle révélation sur les documents classifiés trouvés chez l'ancien président. L'enquête se poursuit.
            </div>
          </div>
          <div className="message-footer">
            <div className="reactions">
              <span className="reaction">😀 156</span>
              <span className="reaction">👍 89</span>
              <span className="reaction">😱 45</span>
              <span className="reaction">❤️ 23</span>
            </div>
            <div className="message-meta">
              <span className="views">👁 18.5K</span>
              <span className="message-time">19:45</span>
            </div>
          </div>
        </div>

        <div className="telegram-message">
          <div className="message-content">
            <div className="post-text">
              💡 TECH UPDATE: Apple annonce de nouvelles fonctionnalités d'IA pour iOS 18. Disponible en beta.
            </div>
          </div>
          <div className="message-footer">
            <div className="reactions">
              <span className="reaction">😀 234</span>
              <span className="reaction">👍 145</span>
              <span className="reaction">🔥 67</span>
            </div>
            <div className="message-meta">
              <span className="views">👁 12.3K</span>
              <span className="message-time">18:30</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramPreview; 